import { type TypeTableColumn } from '@thefarmersfront/kitchen';

import { TextButtonCell, TooltipTextCell } from '~/components/common';

import { formatDate } from '~/utils/date';

import { COLLECTING_SHIPPING_LABEL_STATUS_BADGE_COLOR, DATE_FORMAT } from '~/constants';

import { StatusBadge, joinOrDash } from './deliveryAgencyTableCells';

import type { CollectingResultReportItem } from '~/types/delivery-agency.types';

const getDeliveryAgencyCollectingResultReportsColumnDefs = ({
  currentPage,
  pageSize,
  onClickViewDetail,
}: {
  currentPage: number;
  pageSize: number;
  onClickViewDetail?: (report: CollectingResultReportItem) => void;
}) => {
  const columnDefs: TypeTableColumn<CollectingResultReportItem>[] = [
    {
      id: 'rowNumber',
      header: 'No',
      cell: ({ row }) => row.index + 1 + currentPage * pageSize,
      size: 72,
    },
    {
      accessorKey: 'collectingRequestCode',
      header: '반품요청번호',
      size: 160,
    },
    {
      accessorKey: 'shippingLabelNumber',
      header: '회수 운송장번호',
      size: 232,
      cell: ({ row }) => row.original.shippingLabelNumber || '-',
    },
    // 화면설계: 운송장번호는 텍스트로 두고 상세 진입은 별도 버튼 컬럼으로 분리 (배송 결과 리포트와 동일)
    ...(onClickViewDetail
      ? [
          {
            id: 'viewDetail',
            header: '',
            minSize: 80,
            size: 80,
            cell: ({ row }) => (
              <TextButtonCell showDashIfNoValue={false} onClick={() => onClickViewDetail(row.original)}>
                상세보기
              </TextButtonCell>
            ),
          } satisfies TypeTableColumn<CollectingResultReportItem>,
        ]
      : []),
    {
      accessorKey: 'originalOutboundCode',
      header: '출고번호',
      size: 160,
    },
    {
      accessorKey: 'originalOrderCodes',
      header: '판매처 주문번호(원주문)',
      size: 190,
      cell: ({ row }) => {
        const { originalOrderCodes } = row.original;
        return <TooltipTextCell valueFormatted={joinOrDash(originalOrderCodes)} />;
      },
    },
    {
      accessorKey: 'collectingRequestedDate',
      header: '반품접수일',
      size: 110,
      cell: ({ row }) => formatDate(row.original.collectingRequestedDate, DATE_FORMAT.DATE) ?? '-',
    },
    {
      accessorKey: 'collectingShippingLabelStatus',
      header: '회수 상태',
      size: 120,
      cell: ({ row }) => {
        const value = row.original.collectingShippingLabelStatus;
        if (!value) return '-';

        return <StatusBadge value={value} colors={COLLECTING_SHIPPING_LABEL_STATUS_BADGE_COLOR} />;
      },
    },
    {
      id: 'deliveryLocation',
      accessorFn: (row) => row.collectingSite.deliveryLocation?.description ?? '-',
      header: '회수 요청 장소',
      size: 140,
    },
    {
      id: 'deliveryLocationDetail',
      accessorFn: (row) => row.collectingSite.deliveryLocationDetail || '-',
      header: '회수 요청 장소 상세',
      size: 170,
      cell: ({ getValue }) => <TooltipTextCell value={getValue()} />,
    },
    {
      id: 'collectingFailureReasonCategory',
      accessorFn: (row) => row.collectingFailureReasonCategory?.description ?? '-',
      header: '회수 실패 사유',
      size: 170,
    },
    {
      id: 'contactName',
      accessorFn: (row) => row.contact.name ?? '-',
      header: '원주문 수령자명',
      size: 120,
    },
    {
      id: 'contactTel',
      accessorFn: (row) => row.contact.tel ?? '-',
      header: '고객 연락처',
      size: 140,
    },
    {
      id: 'refineAddress',
      accessorFn: (row) => row.collectingSite.refineAddress ?? '-',
      header: '정제 주소',
      minSize: 220,
      cell: ({ getValue }) => <TooltipTextCell value={getValue()} />,
      meta: { flex: true },
    },
  ];

  return { columnDefs };
};

export default getDeliveryAgencyCollectingResultReportsColumnDefs;
